/* eslint-disable */

// Main imports: React for UI, hooks for state/effects
import React, { useState, useEffect } from 'react';
// Used to switch pages programmatically
import { useNavigate } from 'react-router-dom';
// Service layer for backend API calls
import apiService from '../../services/apiService';
import { fetchAppPagesWithVersions } from '../../services/firestoreService';
// Authentication store: handles user identity info
import { useAuthStore } from '../../store/useAuthStore';
// Profile store for user profile data
import useProfileStore from '../../store/useProfileStore';
// Selected app store
import { useSelectedAppStore } from '../../store/useSelectedAppStore';

// Components
import EmptyState from './EmptyState';
import DeleteConfirmDialog from './DeleteConfirmDialog';
import LeftMenu from '../common/LeftMenu';
// Header component
import Header from '../Header/Header';
// Styles for dashboard
import './Dashboard.css';
// Logging utility for monitoring user actions and errors
import { logger } from "../../utils/logger";

// Enterprise Dashboard - Global dashboard for all applications
const Dashboard = () => {
  // Routing helper for page changes (edit, create, etc)
  const navigate = useNavigate();
  // Stores app list for display, plus loading and errors
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Keeps track of whether delete confirmation dialog is open
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [appToDelete, setAppToDelete] = useState(null);
  // Stats for dashboard
  const [stats, setStats] = useState({
    totalPages: 0,
    totalData: 0,
    totalIntegrations: 0,
    totalProcesses: 0
  });
  const [loadingStats, setLoadingStats] = useState(false);

  // Authentication initialization/read user info from store
  const initAuth = useAuthStore(state => state.init);
  const getUid = useAuthStore(state => state.getUid);
  const user = useAuthStore(state => state.user);
  const { selectedApp, setSelectedApp } = useSelectedAppStore();

  // Log: Dashboard first mounts or rerenders
  logger.log("📂 [Dashboard] Component mounted or updated");

  // Initializes authentication when dashboard loads
  useEffect(() => {
    initAuth?.();
  }, [initAuth]);

  // Loads app list whenever user changes
  useEffect(() => {
    loadApps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  // Load stats when selected app changes
  useEffect(() => {
    if (selectedApp) {
      loadStats(selectedApp);
    } else {
      setStats({
        totalPages: 0,
        totalData: 0,
        totalIntegrations: 0,
        totalProcesses: 0
      });
    }
  }, [selectedApp]);

  // Loads apps for currently logged-in user
  const loadApps = async () => {
    setLoading(true);
    setError(null);

    logger.log("📂 [Dashboard] Loading apps for current user");

    try {
      const uid = getUid?.();
      if (!uid) {
        // User not logged in, show empty
        logger.warn("⚠️ [Dashboard] No user ID found – showing empty app list");
        setApps([]);
        setError('Please sign in to view your applications.');
        setLoading(false);
        return;
      }

      // Log: Starting backend fetch for user-specific apps
      logger.log('🔄 [Dashboard] Fetching apps from backend...' + uid);
      // Call backend service filtering by user
      const fetchedApps = await apiService.getAppsByUser(uid);

      // Store results in state, adjusting for structure
      const allApps = Array.isArray(fetchedApps?.data) ? fetchedApps.data : fetchedApps;
      
      // Get deleted apps from localStorage (client-side tracking)
      const deletedAppsKey = `deleted_apps_${uid}`;
      const deletedAppsFromStorage = JSON.parse(localStorage.getItem(deletedAppsKey) || '[]');
      
      // Merge API apps with deleted apps from storage
      // Only include deleted apps that aren't already in the API response
      const apiAppNames = new Set(allApps.map(app => app.appName || app.name));
      const deletedAppsToAdd = deletedAppsFromStorage.filter(deletedApp => 
        !apiAppNames.has(deletedApp.appName || deletedApp.name)
      );
      
      // Mark deleted apps with isDeleted flag
      const deletedAppsWithFlag = deletedAppsToAdd.map(app => ({
        ...app,
        isDeleted: true,
        deletedAt: app.deletedAt || new Date().toISOString()
      }));
      
      // Combine active apps from API with deleted apps from storage
      const combinedApps = [...allApps, ...deletedAppsWithFlag];
      
      setApps(combinedApps);

      // Auto-select first app if none selected
      const activeApps = combinedApps.filter(app => 
        !app.isDeleted && 
        !app.deletedAt && 
        !app.deleted &&
        app.status !== 'deleted'
      );
      
      if (!selectedApp && activeApps.length > 0) {
        setSelectedApp(activeApps[0]);
      }

      // Log: Number of apps loaded successfully
      logger.log(`📊 [Dashboard] Loaded ${combinedApps.length} apps (${activeApps.length} active)`);
    } catch (err) {
      logger.error('❌ [Dashboard] Failed to load apps: ' + err.message);
      setError('Failed to load applications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load stats for selected app
  const loadStats = async (app) => {
    setLoadingStats(true);
    try {
      const appPrefix = app.appPrefix || app.appName;
      
      // Load pages count
      const pages = await fetchAppPagesWithVersions(appPrefix);
      const uniquePages = new Set(pages.map(p => p.baseName || p.name)).size;
      
      // TODO: Load data count, integrations count, processes count
      // For now, using placeholder values
      setStats({
        totalPages: uniquePages,
        totalData: 0, // TODO: Implement data count
        totalIntegrations: 0, // TODO: Implement integrations count
        totalProcesses: 0 // TODO: Implement processes count
      });
    } catch (err) {
      logger.error('❌ [Dashboard] Failed to load stats: ' + err.message);
    } finally {
      setLoadingStats(false);
    }
  };

  // Navigation helpers
  const handleCreateApp = () => {
    logger.log('📂 [Dashboard] Create button clicked');
    navigate('/create-app');
  };

  const handleOpenAppBuilder = () => {
    if (!selectedApp) return;
    logger.log(`📂 [Dashboard] Open App Builder clicked for app: ${selectedApp.appName}`);
    const appPrefix = selectedApp.appPrefix || selectedApp.appName;
    navigate(`/page-builder?app=${appPrefix}`);
  };

  const handleManageIntegrations = () => {
    if (!selectedApp) return;
    logger.log(`📂 [Dashboard] Manage Integrations clicked for app: ${selectedApp.appName}`);
    navigate(`/integrations?app=${encodeURIComponent(selectedApp.appName)}`);
  };

  const handleViewUsage = () => {
    logger.log('📂 [Dashboard] View Usage & Billing clicked');
    navigate('/billing');
  };

  const handleSelectApp = (app) => {
    setSelectedApp(app);
    logger.log(`📂 [Dashboard] App selected: ${app.appName}`);
  };

  // Opens the delete dialog for the selected app
  const handleDeleteClick = (app) => {
    logger.log(`📂 [Dashboard] Delete initiated for app: ${app.appName}`);
    setAppToDelete(app);
    setDeleteDialogOpen(true);
  };

  // Confirms app deletion and updates list (soft delete)
  const handleDeleteConfirm = async () => {
    if (!appToDelete) return;
    logger.warn(`⚠️ [Dashboard] Delete confirmed for app: ${appToDelete.appName}`);
    try {
      // Backend service call to soft delete app (marks as deleted)
      const success = await apiService.deleteApp(appToDelete.appName);
      if (success) {
        // Store deleted app in localStorage for client-side tracking
        const uid = getUid?.();
        if (uid) {
          const deletedAppsKey = `deleted_apps_${uid}`;
          const deletedApps = JSON.parse(localStorage.getItem(deletedAppsKey) || '[]');
          // Add deleted app with timestamp
          const deletedApp = {
            ...appToDelete,
            isDeleted: true,
            deletedAt: new Date().toISOString()
          };
          // Check if app already exists in deleted list
          const existingIndex = deletedApps.findIndex(app => 
            (app.appName || app.name) === (appToDelete.appName || appToDelete.name)
          );
          if (existingIndex >= 0) {
            deletedApps[existingIndex] = deletedApp;
          } else {
            deletedApps.push(deletedApp);
          }
          localStorage.setItem(deletedAppsKey, JSON.stringify(deletedApps));
          logger.log(`💾 [Dashboard] Saved deleted app to localStorage: ${appToDelete.appName}`);
        }
        
        // Clear selected app if it was deleted
        if (selectedApp && (selectedApp.appName === appToDelete.appName || selectedApp.name === appToDelete.name)) {
          setSelectedApp(null);
        }
        
        // Reload apps to get updated status
        await loadApps();
        setDeleteDialogOpen(false);
        setAppToDelete(null);
        logger.log(`📊 [Dashboard] App soft deleted: ${appToDelete.appName}`);
      } else {
        logger.error('❌ [Dashboard] Failed to delete application (backend)');
        alert('❌ Failed to delete application. Please try again.');
      }
    } catch (err) {
      logger.error(`❌ [Dashboard] Exception during delete: ${err.message}`);
      alert('❌ An error occurred while deleting the application.');
    }
  };

  // Cancels deletion and closes dialog
  const handleDeleteCancel = () => {
    logger.log('📂 [Dashboard] Delete canceled');
    setDeleteDialogOpen(false);
    setAppToDelete(null);
  };

  // Get active apps only
  const getActiveApps = () => {
    return apps.filter(app => 
      !app.isDeleted && 
      !app.deletedAt && 
      !app.deleted &&
      app.status !== 'deleted'
    );
  };

  // Get user's display name
  const getUserDisplayName = () => {
    if (user?.displayName) return user.displayName;
    if (user?.name) return user.name;
    if (user?.email) return user.email.split('@')[0];
    return 'User';
  };

  // Compute visible apps
  const activeApps = getActiveApps();
  const totalAppsCount = activeApps.length;

  // Shows loading screen when data is being fetched
  if (loading) {
    logger.log('📂 [Dashboard] Loading indicator shown');
    return (
      <>
        <Header />
        <LeftMenu />
        <div className="dashboard-container">
          <div className="dashboard-content-wrapper">
            <div className="dashboard-loading">
              <div className="loading-spinner"></div>
              <p>Loading your applications...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Error state if nothing could be loaded
  if (error && !apps.length) {
    logger.warn('⚠️ [Dashboard] Error state displayed: ' + error);
    return (
      <>
        <Header />
        <LeftMenu />
        <div className="dashboard-container">
          <div className="dashboard-content-wrapper">
            <div className="dashboard-error">
              <div className="error-icon">⚠️</div>
              <h2>Nothing here yet</h2>
              <p>{error}</p>
              <button onClick={handleCreateApp} className="btn-create-app">
                <span className="btn-icon">➕</span>Create Your First App
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Empty state when no apps are present
  if (totalAppsCount === 0) {
    logger.log('📂 [Dashboard] Empty state displayed');
    return (
      <>
        <Header />
        <LeftMenu />
        <div className="dashboard-container">
          <div className="dashboard-content-wrapper">
            <EmptyState onCreateApp={handleCreateApp} />
          </div>
        </div>
      </>
    );
  }

  // Main display: Enterprise Dashboard
  return (
    <>
      {/* Unified Header with MUDUMBAI logo and profile icon */}
      <Header />
      <LeftMenu />
      <div className="dashboard-container">
        <div className="dashboard-content-wrapper">
          {/* Welcome Section */}
          <div className="dashboard-welcome">
            <h1 className="welcome-title">
              Welcome back, {getUserDisplayName()}! 👋
            </h1>
            <p className="welcome-subtitle">
              You have <strong>{totalAppsCount}</strong> {totalAppsCount === 1 ? 'application' : 'applications'} in your account
            </p>
          </div>

          {/* High-Level Stats */}
          {selectedApp && (
            <div className="dashboard-stats">
              <div className="stat-card">
                <div className="stat-icon">📱</div>
                <div className="stat-content">
                  <div className="stat-value">{totalAppsCount}</div>
                  <div className="stat-label">Applications</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">📄</div>
                <div className="stat-content">
                  <div className="stat-value">{loadingStats ? '...' : stats.totalPages}</div>
                  <div className="stat-label">Pages</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">🗄️</div>
                <div className="stat-content">
                  <div className="stat-value">{loadingStats ? '...' : stats.totalData}</div>
                  <div className="stat-label">Data</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">🔌</div>
                <div className="stat-content">
                  <div className="stat-value">{loadingStats ? '...' : stats.totalIntegrations}</div>
                  <div className="stat-label">Integrations</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">🔄</div>
                <div className="stat-content">
                  <div className="stat-value">{loadingStats ? '...' : stats.totalProcesses}</div>
                  <div className="stat-label">Processes</div>
                </div>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="dashboard-quick-actions">
            <h2 className="section-title">Quick Actions</h2>
            <div className="quick-actions-grid">
              <button onClick={handleCreateApp} className="quick-action-btn">
                <span className="action-icon">➕</span>
                <span className="action-label">Create New Application</span>
              </button>
              <button 
                onClick={handleOpenAppBuilder} 
                className="quick-action-btn"
                disabled={!selectedApp}
              >
                <span className="action-icon">🎨</span>
                <span className="action-label">Open App Builder</span>
              </button>
              <button 
                onClick={handleManageIntegrations} 
                className="quick-action-btn"
                disabled={!selectedApp}
              >
                <span className="action-icon">🔌</span>
                <span className="action-label">Manage Integrations</span>
              </button>
              <button onClick={handleViewUsage} className="quick-action-btn">
                <span className="action-icon">💳</span>
                <span className="action-label">View Usage & Billing</span>
              </button>
            </div>
          </div>

          {/* Applications Management Section - Simple List */}
          <div className="dashboard-applications">
            <h2 className="section-title">Your Applications</h2>
            {activeApps.length === 0 ? (
              <div className="empty-apps-message">
                <p>No applications yet. Create your first application to get started!</p>
              </div>
            ) : (
              <div className="applications-list-simple">
                {activeApps.map(app => (
                  <div 
                    key={app.appName || app.name}
                    className={`app-list-item-simple ${selectedApp && (selectedApp.appName === app.appName || selectedApp.name === app.name) ? 'selected' : ''}`}
                    onClick={() => handleSelectApp(app)}
                  >
                    <div className="app-list-item-content">
                      <div className="app-list-item-icon">📱</div>
                      <div className="app-list-item-info">
                        <div className="app-list-item-name">{app.appName || app.name}</div>
                        <div className="app-list-item-meta">
                          {app.pageCount || 0} pages • Updated recently
                        </div>
                      </div>
                    </div>
                    <button
                      className="app-list-item-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(app);
                      }}
                      title="Delete application"
                    >
                      🗑️
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pops up delete confirmation if user is removing an app */}
          {deleteDialogOpen && appToDelete && (
            <DeleteConfirmDialog
              app={appToDelete}
              onConfirm={handleDeleteConfirm}
              onCancel={handleDeleteCancel}
            />
          )}
        </div>
      </div>
    </>
  );
};

// Lets other modules use Dashboard component
export default Dashboard;
